/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 */

import React, {
  useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState,
} from 'react';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import LogoRingSpinner from '@/shared/ui/components/basic/loading/LogoRingSpinner';
import { Text } from '@/shared/ui';

/** What the session came back with when a code was put to it. */
export type CouponOutcome =
  | {
    status: 'applied';
    code: string;
    totalCents: number;
    discountCents: number;
    /** 'PERCENT' or 'FIXED', as Payien classifies the coupon. */
    discountType: string | null;
    /** The number behind that type: 20 for 20%, or the cents off. */
    discountValue: number | null;
  }
  /** Payien refused it. `errorCode` is a code, never prose — see below. */
  | { status: 'refused'; errorCode: string }
  /** No session to ask, or a frame that does not know the question. */
  | { status: 'unavailable' };

/** What the host can ask of the frame. */
export interface CheckoutIframeHandle {
  /**
   * Puts a code to the frame, with the price the host is showing for it.
   *
   * The price travels because the frame may not have one. Served
   * fields-only it is a document with no session and no plan behind it, and
   * it used to ask Payien to take 20% off nothing — which came back valid,
   * at a total of nothing, and the summary drew it (BUG-022). The host
   * cannot ask Payien itself: there are no CORS headers on apply-coupon, so
   * the question has to cross the frame.
   *
   * @param code - The code as the buyer typed it.
   * @param priceCents - What "A pagar hoje" shows right now, in cents.
   */
  applyCoupon: (
    code: string, priceCents: number, billingFrequency: string,
  ) => Promise<CouponOutcome>;
  /**
   * Hands the frame the session it should charge against.
   *
   * It delivers an address and nothing else: the charge still needs a click
   * inside Payien's own document, so an arming message that arrived from a
   * script on this page buys nothing.
   *
   * @param sessionId - The session the host just created or already held.
   */
  arm: (sessionId: string) => void;
  /**
   * Says there will be no session for the click that is waiting.
   *
   * The frame gives its button back straight away instead of sitting out
   * its own deadline waiting for an arming message that is never coming.
   */
  refuseArm: () => void;
  /**
   * Asks the frame which of its own fields are not fillable yet.
   *
   * The answer names fields, never values — that is how the host can count
   * what is wrong on both sides of the origin boundary without ever being
   * near a card number.
   *
   * @returns The invalid field names, or an empty list when the frame is
   *   complete or cannot answer.
   */
  requestValidity: () => Promise<string[]>;
}

interface CheckoutIframeProps {
  checkoutUrl: string;
  /**
   * The frame's origin, when the server already resolved one.
   *
   * The sessionless fields frame is addressed by a URL the backend built,
   * and the backend hands back the origin alongside it. Taking it rather
   * than parsing the URL a second time keeps one answer to "which origin is
   * this" instead of two that can drift apart — and the origin is the gate
   * every message from the frame is checked against.
   */
  frameOrigin?: string;
  /**
   * Called when the buyer pressed pay and the frame has no session.
   *
   * The button inside the frame is always live — the handoff removed the
   * locked payment box, and a dead button is the same thing wearing a
   * different shape. So the click can land before the host has a session to
   * point at, and this is the frame saying so.
   */
  onNeedsSession?: () => void;
  /**
   * Told when the buyer pressed pay and the frame's own fields are not ready.
   *
   * The frame marks them and moves focus; the host says what is wrong with
   * each, in the same dialog as its own fields, so the buyer reads one list
   * instead of a tooltip the browser drew inside the frame. Names only.
   */
  onFieldsInvalid?: (fields: string[]) => void;
  /**
   * Told the first time the frame reports a height of its own.
   *
   * Which is the first moment it is actually on screen rather than merely
   * addressed: the host holds its cover until then, so the checkout is
   * revealed whole instead of growing a payment block in front of someone
   * who had already started reading it.
   */
  onReady?: () => void;
  /**
   * Told while the frame is charging the card.
   *
   * The frame's own overlay covers only the frame, so the page around it
   * stayed live during the charge: the CEP, the plan and the coupon were all
   * still editable while the money was moving, and the amount on screen could
   * stop being the amount being charged.
   */
  onBusyChange?: (busy: boolean) => void;
  onPaymentFailed?: (errorCode: string, changeable: boolean) => void;
  onSuccess: () => void;
  onCancel: () => void;
  onExpired?: () => void;
  /**
   * Told whenever the session's discount changes, whoever asked for it.
   *
   * The session is the authority on the discount (see the note on
   * `handleMessage`), so this is the host learning what it now owes rather
   * than the frame requesting anything.
   */
  onCouponApplied?: (outcome: Extract<CouponOutcome, { status: 'applied' }>) => void;
}

const LANG_MAP: Record<string, string> = { 'pt-BR': 'pt', pt: 'pt', en: 'en', es: 'es' };

/**
 * Version stamped on every message this host sends into the frame.
 *
 * The frame refuses an unversioned `checkout_apply_coupon` on purpose:
 * `payien_submit` already exists in production with a different shape in the
 * change-card flow, and without a version there is nothing to tell the two
 * apart. `checkout_measure` is the one exception the frame still tolerates
 * unversioned, because requiring it there would break resizing for every
 * host already deployed.
 */
const PAYIEN_MESSAGE_VERSION = 2;

/**
 * How long to wait for the frame's answer before giving up on the channel.
 *
 * Long enough for Payien to round-trip the coupon service, short enough that
 * a frame which does not implement the message leaves the buyer waiting once
 * rather than on every attempt — after the first silence the channel is
 * marked missing and the host stops asking.
 */
const COUPON_REPLY_MS = 6000;

/**
 * How long to wait for the frame to say which of its fields are wrong.
 *
 * Shorter than the coupon's wait: nothing leaves the browser to answer this
 * one, and the buyer is mid-click while it runs.
 */
const VALIDITY_REPLY_MS = 1500;

/**
 * The height the band holds before the frame has stated one of its own.
 *
 * Two hundred and twenty, not the two-eighty this was: the fields-only
 * document settles at 216 -- a full-width card number, a three-field row
 * under it and the submit button -- and a floor above the settled height is
 * white space under that button for as long as the floor stands. It was
 * only ever a guess at the frame's size, made when the fields were taller,
 * and the guess is cheap to hold close: too low costs a few pixels of
 * growth when the frame reports, too high costs a visible slab.
 */
const FRAME_FLOOR_PX = 306;

/**
 * How long the frame's reported height must hold still before it counts as
 * drawn.
 *
 * The frame's first height report comes from the ResizeObserver's own first
 * callback, which runs as soon as its script does -- with the webfonts still
 * in flight and the card brand icons unpainted. The height changes again
 * when they land, so revealing on that first report put a payment block on
 * screen and then resized it under the buyer's eyes. Waiting for the height
 * to repeat itself waits for the frame to stop moving.
 */
const HEIGHT_SETTLE_MS = 180;

const UNAVAILABLE: CouponOutcome = { status: 'unavailable' };

/**
 * Whether a message really came from the frame we embedded.
 *
 * `event.source` is the hard gate: only the window we embedded can be it,
 * and unlike the origin it does not depend on parsing `checkoutUrl`. An
 * earlier version checked the origin alone, guarded by `expectedOrigin &&` —
 * a malformed or relative URL made it null and dropped the check entirely,
 * so a forged payment_success walked the buyer to activation without paying.
 * The origin stays as a second gate for when we do know it.
 *
 * @param event - The message as it arrived.
 * @param frame - The embedded iframe, once it is mounted.
 * @param expectedOrigin - The session's origin, when the URL parsed.
 * @returns True when the message may be acted on.
 */
function isFromFrame(
  event: MessageEvent, frame: HTMLIFrameElement | null, expectedOrigin: string | null,
): boolean {
  if (!frame || event.source !== frame.contentWindow) return false;
  return hasPayload(event.data) && originAllowed(event.origin, expectedOrigin);
}

/**
 * Whether the message carries something worth reading.
 *
 * @param data - The message payload.
 * @returns True when it is an object.
 */
function hasPayload(data: unknown): boolean {
  return Boolean(data) && typeof data === 'object';
}

/**
 * Whether the origin passes the second gate.
 *
 * @param origin - Where the message says it came from.
 * @param expected - The session's origin, when the URL parsed into one.
 * @returns True when the origin matches, or when there is none to match.
 */
function originAllowed(origin: string, expected: string | null): boolean {
  return !expected || origin === expected;
}

/**
 * Reads the frame's coupon reply into an outcome.
 *
 * @param data - The message payload.
 * @returns What the session says the coupon did.
 */
function couponOutcome(data: Record<string, unknown>): CouponOutcome {
  if (data.type !== 'coupon_applied') {
    return { status: 'refused', errorCode: String(data.error_code ?? '') };
  }
  return {
    status: 'applied',
    code: String(data.code ?? ''),
    totalCents: Number(data.total_cents ?? 0),
    discountCents: Number(data.discount_cents ?? 0),
    discountType: data.discount_type == null ? null : String(data.discount_type),
    discountValue: data.discount_value == null ? null : Number(data.discount_value),
  };
}

const CheckoutIframe = React.forwardRef<CheckoutIframeHandle, CheckoutIframeProps>(({
  checkoutUrl,
  frameOrigin,
  onBusyChange,
  onPaymentFailed,
  onSuccess,
  onCancel,
  onExpired,
  onCouponApplied,
  onNeedsSession,
  onFieldsInvalid,
  onReady,
}, ref) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [frameDrawn, setFrameDrawn] = useState(false);
  /** Whether the frame has ever stated a height, i.e. whether it is drawn. */
  const reported = useRef(false);
  /** Whether the iframe document has fired onLoad for the current localizedUrl. */
  const loadedRef = useRef(false);
  const measureTimersRef = useRef<number[]>([]);
  /** The settle countdown now running, if the height has stopped changing. */
  const settleTimerRef = useRef<number | null>(null);
  /** The last height the frame reported, to tell a repeat from a change. */
  const lastHeightRef = useRef(0);
  const { t } = useTranslation('@pages/onboarding');
  /** Coupon questions waiting on an answer, by request id. */
  const pending = useRef(new Map<number, (outcome: CouponOutcome) => void>());
  const nextRequest = useRef(1);
  /**
   * Set the first time a question goes unanswered.
   *
   * A Payien that does not know the message will not learn it mid-session,
   * and six seconds per attempt is six seconds of spinner for nothing.
   */
  const channelMissing = useRef(false);
  /** The session last handed over, re-sent whenever the frame reloads. */
  const armedSession = useRef<string | null>(null);
  /** Validity questions waiting on an answer, by request id. */
  const waitingValidity = useRef(new Map<number, (fields: string[]) => void>());

  const localizedUrl = useMemo(() => {
    try {
      const url = new URL(checkoutUrl);
      if (!url.searchParams.has('lang')) {
        const lang = LANG_MAP[i18next.language] || i18next.language?.split('-')[0] || 'pt';
        url.searchParams.set('lang', lang);
      }
      url.searchParams.set('embed', '1');
      return url.toString();
    } catch {
      return checkoutUrl;
    }
  }, [checkoutUrl]);

  const expectedOrigin = useMemo(() => {
    if (frameOrigin) return frameOrigin;
    try {
      return new URL(checkoutUrl).origin;
    } catch {
      return null;
    }
  }, [checkoutUrl, frameOrigin]);

  /**
   * Hands a coupon reply to whoever asked, and tells the host either way.
   *
   * @param data - The `coupon_applied` / `coupon_rejected` payload.
   */
  const settleCoupon = useCallback((data: Record<string, unknown>) => {
    const outcome = couponOutcome(data);
    const requestId = Number(data.request_id ?? 0);
    const resolve = pending.current.get(requestId);
    pending.current.delete(requestId);
    resolve?.(outcome);
    // Reported even when nobody is waiting: the session is the authority on
    // the discount, so a change it made on its own is still news the host's
    // summary has to take.
    if (outcome.status === 'applied') onCouponApplied?.(outcome);
  }, [onCouponApplied]);

  /**
   * Hands a validity answer to whoever asked for it.
   *
   * `fields` is a list of names. Payien never sends what was typed into
   * them, and the host has nowhere to put it if it did: the point of the
   * question is to count what is still wrong on both sides of the boundary
   * without the host coming near a card number.
   *
   * @param data - The `payien_validity` payload.
   */
  const settleValidity = useCallback((data: Record<string, unknown>) => {
    const requestId = Number(data.request_id ?? 0);
    const resolve = waitingValidity.current.get(requestId);
    waitingValidity.current.delete(requestId);
    const fields = Array.isArray(data.fields) ? data.fields.map(String) : [];
    resolve?.(data.valid === true ? [] : fields);
  }, []);

  /**
   * What each message from the frame does, by type.
   *
   * ── Who owns the discount ────────────────────────────────────────────
   * `coupon_applied` used to be dropped on the floor, and the reason was
   * sound at the time: the host asked Payien about the code itself and
   * passed it into the session it then built, so the frame could only be
   * repeating a decision already made — and a frame applying a *different*
   * coupon would have put two answers on one screen.
   *
   * The session is the authority now. Payien's apply-coupon revalidates the
   * code against the session it already holds and persists
   * `final_amount_cents` there, and the charge is taken from that number, so
   * what the session says is what the buyer pays — a host that disagreed
   * would simply be wrong on screen. The host keeps the only coupon *field*,
   * so there is still one place to type a code and one answer to read; what
   * it gave up is deciding what that code is worth.
   *
   * The reason still travels as `error_code` and never as prose: Payien
   * names the refusal, the host words it.
   */
  const handlers = useMemo<Record<string, (data: Record<string, unknown>) => void>>(() => ({
    payment_success: () => onSuccess(),
    payment_cancel: () => onCancel(),
    payment_failed: (data) => {
      onBusyChange?.(false);
      onPaymentFailed?.(String(data.error_code ?? 'unknown_error'), Boolean(data.changeable));
    },
    payien_state: (data) => onBusyChange?.(data.state === 'submitting'),
    session_expired: () => onExpired?.(),
    coupon_applied: settleCoupon,
    coupon_rejected: settleCoupon,
    payien_needs_session: () => onNeedsSession?.(),
    payien_fields_invalid: (data) => onFieldsInvalid?.(
      Array.isArray(data.fields) ? data.fields.map(String) : [],
    ),
    payien_validity: settleValidity,
  }), [
    onSuccess, onCancel, onPaymentFailed, onExpired, onBusyChange, settleCoupon,
    onNeedsSession, onFieldsInvalid, settleValidity,
  ]);

  /**
   * Decides whether a reported height means the frame is finished drawing.
   *
   * Two things have to be true. The document must have loaded: before that
   * the only report there can be is the ResizeObserver's first callback,
   * taken off a frame whose fonts have not arrived. And the height must
   * repeat itself: the frame reports again on its own `load`, on
   * `fonts.ready` and every 600ms, and the host asks on top of that, so a
   * height that is still moving is still being reported.
   *
   * @param height - The height the frame just reported.
   */
  const considerDrawn = useCallback((height: number) => {
    if (reported.current || !loadedRef.current) return;
    if (settleTimerRef.current !== null && height !== lastHeightRef.current) {
      window.clearTimeout(settleTimerRef.current);
      settleTimerRef.current = null;
    }
    lastHeightRef.current = height;
    if (settleTimerRef.current !== null) return;
    settleTimerRef.current = window.setTimeout(() => {
      settleTimerRef.current = null;
      reported.current = true;
      setFrameDrawn(true);
      onReady?.();
    }, HEIGHT_SETTLE_MS);
  }, [onReady]);

  const handleMessage = useCallback(
    (event: MessageEvent) => {
      // Every handler below acts on the buyer's behalf, so the sender is
      // checked once, for all of them.
      const frame = iframeRef.current;
      if (!isFromFrame(event, frame, expectedOrigin)) return;

      const data = event.data as Record<string, unknown>;
      if (data.type === 'checkout_resize' && typeof data.height === 'number') {
        const box = frame as HTMLIFrameElement;
        box.style.height = `${data.height}px`;
        considerDrawn(data.height);
        // And let go of the floor. `minHeight` below is for the moment before
        // the frame has said anything; once it has, the floor can only
        // disagree with it, and it disagrees in one direction — a frame that
        // reports *less* than the floor keeps the floor's height and draws a
        // band of empty page under its own last field. That is not a corner
        // case: the card fields are a three-column grid now, and the shrink
        // from a stacked column to two rows takes the frame well under 280.
        box.style.minHeight = '0px';
      }
      handlers[String(data.type)]?.(data);
    },
    [handlers, expectedOrigin, considerDrawn],
  );

  /**
   * Puts a code to the open session, without rebuilding anything.
   *
   * @param code - The code as the buyer typed it.
   * @param priceCents - The summary's current total, for a frame with no price.
   * @param billingFrequency - The cycle the buyer is actually buying.
   * @returns What the session made of it.
   */
  const applyCoupon = useCallback((
    code: string, priceCents: number, billingFrequency: string,
  ): Promise<CouponOutcome> => {
    const target = iframeRef.current?.contentWindow;
    if (!target || !expectedOrigin || channelMissing.current) {
      return Promise.resolve(UNAVAILABLE);
    }
    const requestId = nextRequest.current;
    nextRequest.current += 1;
    target.postMessage(
      {
        v: PAYIEN_MESSAGE_VERSION,
        type: 'checkout_apply_coupon',
        request_id: requestId,
        code,
        price_cents: priceCents,
        // Sent for the same reason the price is: fields-only, the frame was
        // served before a plan existed and its own cycle defaults to
        // monthly. A coupon restricted to one cycle was therefore measured
        // against the wrong one -- a monthly-only code took 20% off a yearly
        // plan, and the refusal only surfaced as a 500 at payment (BUG-025).
        billing_frequency: billingFrequency,
      },
      expectedOrigin,
    );
    return new Promise((resolve) => {
      pending.current.set(requestId, resolve);
      window.setTimeout(() => {
        if (!pending.current.delete(requestId)) return;
        channelMissing.current = true;
        resolve(UNAVAILABLE);
      }, COUPON_REPLY_MS);
    });
  }, [expectedOrigin]);

  /**
   * Posts a versioned message into the frame, when there is one to post to.
   *
   * @param message - The message body, without its version stamp.
   * @returns True when it went out.
   */
  const post = useCallback((message: Record<string, unknown>): boolean => {
    const target = iframeRef.current?.contentWindow;
    if (!target || !expectedOrigin) return false;
    target.postMessage({ v: PAYIEN_MESSAGE_VERSION, ...message }, expectedOrigin);
    return true;
  }, [expectedOrigin]);

  /**
   * Tells the frame which session it is charging against.
   *
   * @param sessionId - The session the host holds.
   */
  /**
   * Tells the frame there will be no session for the click it just made.
   *
   * The host validates its own fields the instant the click lands, so when
   * they are incomplete it knows at once. Without this the only way to say
   * so was silence, and the buyer sat out the frame's full thirty-second
   * deadline to learn that their own CEP was missing.
   *
   * Which host fields are wrong is not sent: the frame marks its own and
   * reaches into no others.
   */
  const refuseArm = useCallback(() => {
    armedSession.current = null;
    post({ type: 'payien_arm_refused' });
  }, [post]);

  const arm = useCallback((sessionId: string) => {
    // Remembered, not just sent. The frame is born sessionless and the
    // session can be handed over at any point after that, including before
    // the document inside the frame has finished listening — a message
    // posted into a window that is still parsing is simply lost, and the
    // buyer would press pay on a frame that had been armed as far as the
    // host knew.
    armedSession.current = sessionId;
    if (loadedRef.current) post({ type: 'payien_arm', session_id: sessionId });
  }, [post]);

  /**
   * Asks the frame whether its own fields are fillable.
   *
   * @returns The invalid field names, empty when the frame is complete or
   *   cannot answer.
   */
  const requestValidity = useCallback((): Promise<string[]> => {
    const requestId = nextRequest.current;
    nextRequest.current += 1;
    if (!post({ type: 'payien_validate', request_id: requestId })) {
      return Promise.resolve([]);
    }
    return new Promise((resolve) => {
      waitingValidity.current.set(requestId, resolve);
      // A frame that does not know the question must not hold the buyer's
      // click: an unanswered validity check means "nothing to report", and
      // the frame still paints its own fields.
      window.setTimeout(() => {
        if (waitingValidity.current.delete(requestId)) resolve([]);
      }, VALIDITY_REPLY_MS);
    });
  }, [post]);

  useImperativeHandle(
    ref,
    () => ({ applyCoupon, arm, refuseArm, requestValidity }),
    [applyCoupon, arm, refuseArm, requestValidity],
  );

  // A question put to a session the buyer has left behind can never be
  // answered, and a promise nobody settles leaves the coupon button spinning
  // for the rest of the page's life.
  const waiting = pending.current;
  const waitingFields = waitingValidity.current;
  useEffect(() => {
    loadedRef.current = false;
    lastHeightRef.current = 0;
    return () => {
      measureTimersRef.current.forEach(window.clearTimeout);
      measureTimersRef.current = [];
      if (settleTimerRef.current !== null) {
        window.clearTimeout(settleTimerRef.current);
        settleTimerRef.current = null;
      }
      waiting.forEach((resolve) => resolve(UNAVAILABLE));
      waiting.clear();
      waitingFields.forEach((resolve) => resolve([]));
      waitingFields.clear();
    };
  }, [waiting, waitingFields, localizedUrl]);

  useEffect(() => {
    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [handleMessage]);

  // Ask, rather than only listen. The frame reports its own height, and when
  // that report does not arrive the frame stays at the floor below and
  // `scrolling="no"` cuts the submit button off with nothing able to scroll
  // to it. Asking a few times over the first seconds after load covers the
  // window where the frame is still laying out -- after that its own tick
  // keeps up.
  const askForHeight = useCallback(() => {
    const frame = iframeRef.current;
    if (!loadedRef.current || !frame?.contentWindow || !expectedOrigin) return;
    frame.contentWindow.postMessage(
      { v: PAYIEN_MESSAGE_VERSION, type: 'checkout_measure' }, expectedOrigin,
    );
  }, [expectedOrigin]);

  /**
   * Measures the frame, and tells it again which session it is charging.
   *
   * Both exist for the same reason: a document that has just loaded has
   * heard nothing the host said before it was listening.
   */
  const handleLoad = useCallback(() => {
    loadedRef.current = true;
    measureTimersRef.current.forEach(window.clearTimeout);
    measureTimersRef.current = [0, 300, 900, 2000].map(
      (delay) => window.setTimeout(askForHeight, delay),
    );
    if (armedSession.current) post({ type: 'payien_arm', session_id: armedSession.current });
  }, [askForHeight, post]);

  // One frame, in the flow, at whatever height it reports. The phone used
  // to get a fixed overlay that covered everything under the header and
  // locked the page's scroll, so the buyer lost the summary, the seat
  // chooser and the form they were still filling -- and could not scroll
  // back to any of them. That existed because a cross-origin frame pinned
  // inside a fixed box does not reliably scroll on touch; now the frame
  // measures its own content and the page scrolls as a page.
  return (
    <div
      data-testid="checkout-iframe-container"
      style={{
        position: 'relative',
        /*
         * The floor holds the band open while the cover is up, and is let
         * go the moment the frame states a height of its own.
         *
         * Keeping it was a slab of empty page under the submit button. The
         * frame's own `minHeight` was already released on the first report
         * -- that is what the note beside `box.style.minHeight` is about --
         * but this wrapper's was not, and the wrapper is what the card
         * measures. A frame that settles under the floor, which the card
         * fields do at 230, left the difference as white space the buyer
         * reads as the page having ended early.
         */
        minHeight: frameDrawn ? 0 : FRAME_FLOOR_PX,
      }}
    >
      {!frameDrawn && (
        <div
          data-testid="checkout-iframe-loading"
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 'var(--space-3)',
            minHeight: FRAME_FLOOR_PX,
            background: 'var(--surface-primary)',
            borderRadius: 8,
            zIndex: 1,
            pointerEvents: 'none',
          }}
        >
          <LogoRingSpinner />
          <Text style={{ fontSize: 'var(--text-meta)', color: 'var(--text-tertiary)' }}>
            {t('checkoutStep.payment.preparing', { defaultValue: 'Carregando pagamento...' })}
          </Text>
        </div>
      )}
      <iframe
        ref={iframeRef}
        src={localizedUrl}
        onLoad={handleLoad}
        title={t('checkout.iframeTitle')}
        sandbox="allow-forms allow-scripts allow-same-origin allow-popups allow-top-navigation"
        scrolling="no"
        className="w-full checkout-iframe"
        style={{
          maxWidth: 1200,
          border: 'none',
          borderRadius: 8,
          background: 'transparent',
          margin: '0 auto',
          display: 'block',
          opacity: frameDrawn ? 1 : 0,
        }}
      />
    </div>
  );
});

CheckoutIframe.displayName = 'CheckoutIframe';

export default CheckoutIframe;
